"use client";

import { useState, useRef } from "react";
import Image from "next/image";
import {
  GenerateResponse,
  VideoResponse,
  getImageUrlWithAuth,
  getVideoUrlWithAuth,
  deleteImage,
  deleteVideo,
} from "@/lib/api";
import { Clock, X, Loader2, ChevronUp, ChevronDown, ImageIcon, Video, Upload, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

type FilterType = "images" | "videos";

interface HistoryGalleryProps {
  images: GenerateResponse[];
  videos?: VideoResponse[];
  onSelect: (image: GenerateResponse) => void;
  onSelectVideo?: (video: VideoResponse) => void;
  onDelete?: (imageId: string) => void;
  onDeleteVideo?: (videoId: string) => void;
  onUpload?: (file: File) => Promise<void>;
  isUploading?: boolean;
  selectedId?: string;
  selectedVideoId?: string;
}

const ITEM_SIZE = 64; // px per item
const GAP_SIZE = 8; // gap between items

export function HistoryGallery({
  images,
  videos = [],
  onSelect,
  onSelectVideo,
  onDelete,
  onDeleteVideo,
  onUpload,
  isUploading = false,
  selectedId,
  selectedVideoId,
}: HistoryGalleryProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>("images");
  const [isDragOver, setIsDragOver] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onUpload) {
      try {
        await onUpload(file);
      } catch (error) {
        console.error("Upload failed:", error);
      }
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (filter === "images" && onUpload) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (filter !== "images" || !onUpload) return;

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      try {
        await onUpload(file);
      } catch (error) {
        console.error("Upload failed:", error);
      }
    }
  };

  const handleDeleteImage = async (e: React.MouseEvent, imageId: string) => {
    e.stopPropagation();

    if (!confirm("Delete this image?")) return;

    setDeletingId(imageId);
    try {
      await deleteImage(imageId);
      onDelete?.(imageId);
    } catch (error) {
      console.error("Failed to delete:", error);
      alert("Failed to delete image");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteVideo = async (e: React.MouseEvent, videoId: string) => {
    e.stopPropagation();

    if (!confirm("Delete this video?")) return;

    setDeletingId(videoId);
    try {
      await deleteVideo(videoId);
      onDeleteVideo?.(videoId);
    } catch (error) {
      console.error("Failed to delete:", error);
      alert("Failed to delete video");
    } finally {
      setDeletingId(null);
    }
  };

  const scrollUp = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        top: -(ITEM_SIZE + GAP_SIZE) * 2,
        behavior: "smooth",
      });
    }
  };

  const scrollDown = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        top: (ITEM_SIZE + GAP_SIZE) * 2,
        behavior: "smooth",
      });
    }
  };

  // Filter completed videos only for display
  const completedVideos = videos.filter((v) => v.status === "completed");

  const isEmpty =
    filter === "images" ? images.length === 0 : completedVideos.length === 0;

  const emptyMessage =
    filter === "images" ? "No images yet" : "No videos yet";

  return (
    <div
      className={`flex flex-col gap-2 h-full relative ${
        isDragOver ? "ring-2 ring-primary ring-inset" : ""
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Drag overlay */}
      {isDragOver && (
        <div className="absolute inset-0 bg-primary/10 border-2 border-dashed border-primary rounded-lg z-10 flex items-center justify-center">
          <div className="text-center">
            <Upload className="h-8 w-8 mx-auto mb-2 text-primary" />
            <p className="text-sm font-medium text-primary">Drop image here</p>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <Tabs
        value={filter}
        onValueChange={(v) => setFilter(v as FilterType)}
        className="w-full shrink-0"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="images" className="gap-1.5 text-xs">
            <ImageIcon className="h-3.5 w-3.5" />
            Images ({images.length})
          </TabsTrigger>
          <TabsTrigger value="videos" className="gap-1.5 text-xs">
            <Video className="h-3.5 w-3.5" />
            Videos ({completedVideos.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Upload Button - Images tab only */}
      {filter === "images" && onUpload && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleUploadClick}
          disabled={isUploading}
          className="w-full gap-2 shrink-0"
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          {isUploading ? "Uploading..." : "Upload Image"}
        </Button>
      )}

      {isEmpty ? (
        <div className="text-center py-8 text-muted-foreground flex-1 flex flex-col items-center justify-center">
          <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{emptyMessage}</p>
        </div>
      ) : (
        <>
          {/* Scroll Up Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={scrollUp}
            className="w-full h-8 hover:bg-secondary shrink-0"
          >
            <ChevronUp className="h-4 w-4" />
          </Button>

          {/* Scrollable Grid Container */}
          <div
            ref={scrollContainerRef}
            className="overflow-y-auto scrollbar-thin scrollbar-thumb-secondary scrollbar-track-transparent flex-1"
          >
            <div className="grid grid-cols-4 gap-2">
              {filter === "images"
                ? images.map((image) => {
                    const imageUrl = getImageUrlWithAuth(image.image_id);
                    const isSelected = selectedId === image.image_id;
                    const isDeleting = deletingId === image.image_id;

                    return (
                      <div
                        key={image.image_id}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all group ${
                          isSelected
                            ? "border-primary ring-2 ring-primary/50"
                            : "border-transparent hover:border-primary/50"
                        } ${isDeleting ? "opacity-50" : "hover:scale-105 hover:shadow-lg"}`}
                      >
                        <button
                          onClick={() => onSelect(image)}
                          className="relative w-full h-full"
                          disabled={isDeleting}
                        >
                          <Image
                            src={imageUrl}
                            alt={image.prompt}
                            fill
                            className="object-cover"
                            sizes="64px"
                            unoptimized
                          />
                        </button>

                        {/* Upload badge if user uploaded */}
                        {image.is_uploaded && (
                          <div className="absolute bottom-1 left-1 bg-blue-600/80 rounded px-1 py-0.5">
                            <Upload className="h-2.5 w-2.5 text-white" />
                          </div>
                        )}

                        {/* Video badge if video exists */}
                        {image.video_id && image.video_status === "completed" && (
                          <div className={`absolute bottom-1 ${image.is_uploaded ? "left-6" : "left-1"} bg-green-600/80 rounded px-1 py-0.5`}>
                            <Video className="h-2.5 w-2.5 text-white" />
                          </div>
                        )}

                        {/* Edit badge if image is edited */}
                        {image.is_edited && (
                          <div className={`absolute bottom-1 ${
                            image.is_uploaded && image.video_id && image.video_status === "completed" ? "left-11" :
                            (image.is_uploaded || (image.video_id && image.video_status === "completed")) ? "left-6" : "left-1"
                          } bg-purple-600/80 rounded px-1 py-0.5`}>
                            <Pencil className="h-2.5 w-2.5 text-white" />
                          </div>
                        )}

                        {/* Delete button */}
                        <button
                          onClick={(e) => handleDeleteImage(e, image.image_id)}
                          disabled={isDeleting}
                          className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          {isDeleting ? (
                            <Loader2 className="h-3 w-3 text-white animate-spin" />
                          ) : (
                            <X className="h-3 w-3 text-white" />
                          )}
                        </button>
                      </div>
                    );
                  })
                : completedVideos.map((video) => {
                    const videoUrl = getVideoUrlWithAuth(video.video_id);
                    const isSelected = selectedVideoId === video.video_id;
                    const isDeleting = deletingId === video.video_id;

                    return (
                      <div
                        key={video.video_id}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all group ${
                          isSelected
                            ? "border-primary ring-2 ring-primary/50"
                            : "border-transparent hover:border-primary/50"
                        } ${isDeleting ? "opacity-50" : "hover:scale-105 hover:shadow-lg"}`}
                      >
                        <button
                          onClick={() => onSelectVideo?.(video)}
                          className="w-full h-full bg-secondary/50 flex items-center justify-center"
                          disabled={isDeleting}
                        >
                          <video
                            src={videoUrl}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                            onMouseEnter={(e) => e.currentTarget.play()}
                            onMouseLeave={(e) => {
                              e.currentTarget.pause();
                              e.currentTarget.currentTime = 0;
                            }}
                          />
                        </button>

                        {/* Video indicator */}
                        <div className="absolute bottom-1 left-1 bg-primary/80 rounded px-1 py-0.5">
                          <Video className="h-2.5 w-2.5 text-white" />
                        </div>

                        {/* Delete button */}
                        <button
                          onClick={(e) => handleDeleteVideo(e, video.video_id)}
                          disabled={isDeleting}
                          className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          {isDeleting ? (
                            <Loader2 className="h-3 w-3 text-white animate-spin" />
                          ) : (
                            <X className="h-3 w-3 text-white" />
                          )}
                        </button>
                      </div>
                    );
                  })}
            </div>
          </div>

          {/* Scroll Down Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={scrollDown}
            className="w-full h-8 hover:bg-secondary shrink-0"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );
}
